import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../api";

const Ctx = createContext(null);
export function usePagos() {
  const v = useContext(Ctx);
  if (!v) throw new Error("usePagos debe usarse dentro de <PagosProvider>");
  return v;
}

export function PagosProvider({ children, session }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [pagos, setPagos] = useState([]); // combined worth + jpgm
  const [catalogo, setCatalogo] = useState([]);
  const [valuadores, setValuadores] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);

  async function refreshAll() {
    if (!session?.token) return;
    setLoading(true);
    setError("");
    try {
      const [cat, vals, ba, exps] = await Promise.all([
        api.listCatalogo(),
        api.listValuadores(),
        api.listBankAccounts(),
        api.listExpenses({}),
      ]);
      setCatalogo(cat);
      setValuadores(vals);
      setBankAccounts(ba);
      setPagos(exps.map((e) => ({
        // compat con componentes existentes
        id: e.id,
        scope: e.scope,
        responsable: e.scope === 'jpgm' ? 'JPGM' : 'Worth',
        nombre: e.name,
        fecha: e.date,
        fechaAjustada: e.date,
        monto: Number(e.amount),
        paid_amount: Number(e.paid_amount || 0),
        status: e.status,
        clasificacion: e.classification,
        ordenServicio: e.orden_servicio || '',
        cuentaPago: e.payment_account_name || '',
        cuentaDestino: e.destination_account_name || '',
        payment_account_id: e.payment_account_id,
        destination_account_id: e.destination_account_id,
        valuador: e.valuador_name || '',
        valuador_id: e.valuador_id,
        catalog_account_id: e.catalog_account_id,
        catalog_path: [e.level1, e.level2, e.level3, e.catalog_name].filter(Boolean).join(' / '),
      })));
    } catch (e) {
      setError(e.message || 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.token]);

  const cuentasPagoObj = useMemo(() => {
    const out = { Worth: [], JPGM: [] };
    for (const a of bankAccounts) {
      if (a.direction !== 'out') continue;
      if (a.scope === 'worth') out.Worth.push(a);
      if (a.scope === 'jpgm') out.JPGM.push(a);
    }
    out.Worth.sort((a,b)=>a.name.localeCompare(b.name));
    out.JPGM.sort((a,b)=>a.name.localeCompare(b.name));
    return out;
  }, [bankAccounts]);

  const cuentasPago = useMemo(() => ({
    Worth: (cuentasPagoObj.Worth || []).map((x) => x.name),
    JPGM: (cuentasPagoObj.JPGM || []).map((x) => x.name),
  }), [cuentasPagoObj]);

  const cuentasDestinoObj = useMemo(() => {
    const out = { Worth: [], JPGM: [] };
    for (const a of bankAccounts) {
      if (a.direction !== 'in') continue;
      if (a.scope === 'worth') out.Worth.push(a);
      if (a.scope === 'jpgm') out.JPGM.push(a);
    }
    out.Worth.sort((a,b)=>a.name.localeCompare(b.name));
    out.JPGM.sort((a,b)=>a.name.localeCompare(b.name));
    return out;
  }, [bankAccounts]);

  const cuentasDestino = useMemo(() => ({
    Worth: (cuentasDestinoObj.Worth || []).map((x) => x.name),
    JPGM: (cuentasDestinoObj.JPGM || []).map((x) => x.name),
  }), [cuentasDestinoObj]);

  // Clasificación (solo para dropdown corto). Se puede ajustar a catálogo contable si así lo quieren.
  const categorias = useMemo(() => {
    return {
      Worth: ["Nómina", "Gastos de Operación", "Gastos de Administración", "Otros"],
      JPGM: ["Personales", "Viaje", "Otros"],
    };
  }, []);

  // Helpers pagos
  const getPagadoMonto = (id, scope) => {
    const p = pagos.find((x) => x.id === id && x.scope === scope);
    return Number(p?.paid_amount || 0);
  };
  const getPendienteMonto = (p) => Math.max(0, Number(p.monto) - Number(p.paid_amount || 0));

  // API actions
  const addPago = async (nuevo) => {
    const scope = (nuevo.responsable || '').toLowerCase() === 'jpgm' ? 'jpgm' : 'worth';
    const payload = {
      name: nuevo.nombre,
      date: nuevo.fecha,
      amount: Number(nuevo.monto),
      catalog_account_id: Number(nuevo.catalog_account_id),
      classification: nuevo.clasificacion,
      orden_servicio: nuevo.ordenServicio || '',
      payment_account_id: Number(nuevo.payment_account_id),
      destination_account_id: Number(nuevo.destination_account_id),
      valuador_id: nuevo.valuador_id ? Number(nuevo.valuador_id) : null,
    };
    await api.createExpense(scope, payload);
    await refreshAll();
  };

  const updatePagoScoped = async (scope, id, patch) => {
    await api.updateExpense(scope, id, patch);
    await refreshAll();
  };

  const setPagadoPorcentajeScoped = async (scope, id, porcentaje) => {
    const p = pagos.find((x) => x.id === id && x.scope === scope);
    if (!p) return;
    const pct = Math.max(0, Math.min(100, Number(porcentaje) || 0));
    const monto = Math.round((Number(p.monto) * pct) / 100);
    await api.setPaidAmount(scope, id, monto);
    await refreshAll();
  };

  const setPagadoMontoScoped = async (scope, id, monto) => {
    await api.setPaidAmount(scope, id, Number(monto) || 0);
    await refreshAll();
  };

  const togglePagadoTotalScoped = async (scope, id) => {
    const p = pagos.find((x) => x.id === id && x.scope === scope);
    if (!p) return;
    const paid = Number(p.paid_amount || 0);
    await api.setPaidAmount(scope, id, paid >= Number(p.monto) ? 0 : Number(p.monto));
    await refreshAll();
  };

  const decidirScoped = async (scope, id, status) => {
    await api.decideExpense(scope, id, status);
    await refreshAll();
  };

  const rolloverPagoScoped = async (scope, id, target_day) => {
    await api.rollover(scope, id, Number(target_day));
    await refreshAll();
  };

  
  // ---- Compat con componentes MVP (firma antigua) ----
  const scopeOf = (id) => (pagos.find((x) => x.id === id)?.scope) || 'worth';

  const updatePago = async (id, patch) => updatePagoScoped(scopeOf(id), id, patch);
  const setPagadoPorcentaje = async (id, porcentaje) => setPagadoPorcentajeScoped(scopeOf(id), id, porcentaje);
  const setPagadoMonto = async (id, monto) => setPagadoMontoScoped(scopeOf(id), id, monto);
  const togglePagadoTotal = async (id) => togglePagadoTotalScoped(scopeOf(id), id);
  const decidir = async (id, status) => decidirScoped(scopeOf(id), id, status);
  const rolloverPago = async (id, target_day) => rolloverPagoScoped(scopeOf(id), id, target_day);

  const aplicarRolloverMes = async (anio, mes0, targetDay = null) => {
    // mueve todos los pendientes del mes seleccionado al mes siguiente
    const pend = pagos.filter((p) => {
      const d = new Date(p.fecha);
      return d.getFullYear() === anio && d.getMonth() === mes0 && getPendienteMonto(p) > 0;
    });
    for (const p of pend) {
      const day = targetDay ?? new Date(p.fecha).getDate();
      await rolloverPagoScoped(p.scope, p.id, day);
    }
    await refreshAll();
  };

const value = useMemo(
    () => ({
      loading,
      error,

      pagos,
      catalogo,
      valuadores,
      bankAccounts,
      categorias,
      cuentasPago,
      cuentasDestino,
      cuentasPagoObj,
      cuentasDestinoObj,

      getPagadoMonto,
      getPendienteMonto,

      addPago,
      updatePago,

      // scoped (uso interno)
      updatePagoScoped,

      setPagadoPorcentaje,
      setPagadoPorcentajeScoped,
      setPagadoMonto,
      setPagadoMontoScoped,
      togglePagadoTotal,
      togglePagadoTotalScoped,

      decidir,
      decidirScoped,
      rolloverPago,
      rolloverPagoScoped,

      refreshAll,
      session,
    }),
    [loading, error, pagos, catalogo, valuadores, bankAccounts, categorias, cuentasPago, cuentasDestino, session]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
